import { type ReactNode, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  useAcceptCommunityInvitationMutation,
  useGetCommunityInvitationResolutionQuery,
} from '@/services/CommunityUserService';
import { RootState } from '@/store/store';
import { logout, setSelectedCommunityId } from '@/store/authSlice';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof (error as { data?: { message?: string } }).data?.message === 'string'
  ) {
    return (error as { data: { message: string } }).data.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const InvitationCard = ({
  children,
  maxWidth = 'max-w-[560px]',
  spacing = 'space-y-4',
}: {
  children: ReactNode;
  maxWidth?: string;
  spacing?: string;
}) => (
  <main className="fixed inset-0 flex min-h-screen w-screen items-center justify-center bg-gray-100 px-4 py-8 text-center">
    <div className={`card w-full ${maxWidth} mx-auto`}>
      <div
        className={`card-body flex flex-col items-center justify-center p-10 text-center ${spacing}`}
      >
        {children}
      </div>
    </div>
  </main>
);

const CommunityInvitationAcceptPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('token') || '').trim();
  const authToken = useSelector((state: RootState) => state.auth.token);
  const userData = useSelector((state: RootState) => state.auth.userData);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const {
    data: invitation,
    isLoading,
    error,
  } = useGetCommunityInvitationResolutionQuery(token, {
    skip: !token,
    refetchOnMountOrArgChange: true,
  });
  const [acceptInvitation, { isLoading: isAccepting }] =
    useAcceptCommunityInvitationMutation();

  const invitedEmailMismatch = useMemo(() => {
    if (!invitation || !userData?.email) return false;

    return (
      userData.email.trim().toLowerCase() !== invitation.email.toLowerCase()
    );
  }, [invitation, userData?.email]);

  const loginReturnState = {
    from: {
      pathname: location.pathname,
      search: location.search,
    },
  };

  const handleSignIn = () => {
    navigate('/auth/login', {
      state: loginReturnState,
    });
  };

  const handleSwitchAccount = () => {
    dispatch(logout());
    navigate('/auth/login', {
      replace: true,
      state: loginReturnState,
    });
  };

  const handleAcceptInvitation = async () => {
    if (
      !invitation ||
      invitation.portal !== 'admin' ||
      invitation.status !== 'active'
    ) {
      return;
    }

    if (!authToken) {
      handleSignIn();
      return;
    }

    if (invitedEmailMismatch) {
      return;
    }

    setAcceptError(null);
    await acceptInvitation(token)
      .unwrap()
      .then(() => {
        dispatch(
          setSelectedCommunityId({
            selectedCommunityId: invitation.community_id,
          })
        );
        enqueueSnackbar('Community invitation accepted', {
          variant: 'success',
        });
        navigate('/community/details?tab=users-management', { replace: true });
      })
      .catch((acceptErr) => {
        setAcceptError(
          getErrorMessage(
            acceptErr,
            'We could not accept this invitation. Please try again.'
          )
        );
      });
  };

  if (!token) {
    return (
      <InvitationCard maxWidth="max-w-[480px]" spacing="space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">
          Invitation link is incomplete
        </h1>
        <p className="text-sm text-gray-600">
          The community invitation token is missing from this link.
        </p>
      </InvitationCard>
    );
  }

  if (isLoading) {
    return (
      <InvitationCard maxWidth="max-w-[480px]" spacing="space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">
          Opening invitation
        </h1>
        <p className="text-sm text-gray-600">
          We&apos;re checking which Saaristo portal should handle this
          invitation.
        </p>
      </InvitationCard>
    );
  }

  if (error || !invitation) {
    return (
      <InvitationCard maxWidth="max-w-[480px]" spacing="space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">
          Invitation could not be opened
        </h1>
        <p className="text-sm text-gray-600">
          {getErrorMessage(
            error,
            'This invitation may be invalid or no longer available.'
          )}
        </p>
      </InvitationCard>
    );
  }

  if (invitation.portal === 'frontend') {
    return (
      <InvitationCard maxWidth="max-w-[520px]" spacing="space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">
          Open this invitation in the user portal
        </h1>
        <p className="text-sm text-gray-600">
          This community invitation belongs in the Saaristo user app. Continue
          there to review and accept it.
        </p>
        <a href={invitation.redirect_url} className="btn btn-primary">
          Continue to user portal
        </a>
      </InvitationCard>
    );
  }

  if (invitedEmailMismatch) {
    return (
      <InvitationCard>
        <h1 className="text-xl font-semibold text-gray-900">
          Sign in with the invited admin account
        </h1>
        <p className="text-sm text-gray-600">
          This invitation is for{' '}
          <span className="font-medium">{invitation.email}</span>, but you are
          currently signed in as{' '}
          <span className="font-medium">{userData?.email}</span>.
        </p>
        <button
          type="button"
          onClick={handleSwitchAccount}
          className="btn btn-primary"
        >
          Sign in with invited account
        </button>
      </InvitationCard>
    );
  }

  if (invitation.status !== 'active') {
    return (
      <InvitationCard maxWidth="max-w-[520px]" spacing="space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">
          Invitation is no longer active
        </h1>
        <p className="text-sm text-gray-600">
          Status:{' '}
          <span className="font-medium capitalize">{invitation.status}</span>
        </p>
      </InvitationCard>
    );
  }

  return (
    <InvitationCard>
      <h1 className="text-xl font-semibold text-gray-900">
        Community invitation
      </h1>
      <p className="text-sm text-gray-600">
        You&apos;ve been invited to join{' '}
        <span className="font-medium">{invitation.community_name}</span> as{' '}
        <span className="font-medium">{invitation.role}</span>.
      </p>
      <p className="text-sm text-gray-600">
        {authToken
          ? 'Accepting will add this community to your admin access.'
          : 'Sign in with the invited admin account to continue.'}
      </p>
      <button
        type="button"
        className="btn btn-primary"
        disabled={isAccepting}
        onClick={authToken ? handleAcceptInvitation : handleSignIn}
      >
        {isAccepting
          ? 'Accepting invitation...'
          : authToken
            ? 'Accept Invitation'
            : 'Sign in to accept invitation'}
      </button>
      {acceptError ? (
        <p className="text-sm text-danger">{acceptError}</p>
      ) : null}
    </InvitationCard>
  );
};

export default CommunityInvitationAcceptPage;
